/**
 * External Imports
 */
import { FC, Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuTooltip } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import AddBtn from './addBtn';
import ExportCsvBtn from './exportCsvBtn';
import DischargeBtn from './dischargeBtn/DischargeBtn';
import FilterBtn from './filterBtn';
import ProvisioningBtn from './provisioningBtn';
import DownloadBtn from '../shared/buttons/DownloadBtn';
import SearchEmployeesBtn from './searchEmployeesBtn';
import SearchField from './searchField';
import SelectTemplateDropDown from './selectTemplatesDropdown';
import UnlistedRoundBtn from './unlistedRoundBtn';

import { openModal, setModalAction, setModalType } from '../../redux/actions';

import { logTaskClick } from '../../utils/analyticsHelpers';

/**
 * Global Type Definition Imports
 */

/**
 * Style Imports
 */
import { HelpIcon, HintSection, SearchHeaderBar } from './SearchHeader.styles';
import { useUserRolesRef } from '../../services/UserRoles/userRoles';
import DownloadExportCsvBtn from './downloadExportCsvBtn/DownloadExportCsvBtn';

interface SearchHeaderProps {
  searchTerm: string;
  isShiftStatusDataAvailable?: boolean;
  handleExportCsv?: () => void;
  handleClickDownloadPDF?: () => void;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const SearchHeader: FC<SearchHeaderProps> = ({ ...props }) => {
  const dispatch = useDispatch();

  const location = useLocation();
  const { '*': view } = useParams();

  const { logTrackingEvent } = useAnalyticsApi();

  const { isEnterpriseAdmin } = useUserRolesRef();

  const handleAddClick = (addType: string) => {
    if (view) {
      logTrackingEvent(logTaskClick(view, 'add'));
    } else {
      console.log('No matching type for event!');
    }
    if (addType === 'Add Feedback') {
      dispatch(setModalAction('ADD')); // For feedback add modal
    }
    dispatch(setModalType(addType));
    dispatch(openModal());
  };

  const taskRoundingTypePrefix = useMemo(() => {
    if (location.pathname.includes('patients')) return 'patient-';
    if (location.pathname.includes('employees')) return 'employee-';
    return '';
  }, [location.pathname]);

  useEffect(() => {
    props.setSearchTerm('');
  }, [location]);

  return (
    <SearchHeaderBar
      id="Search-Header"
      data-testid={view === 'employee' ? 'employee-search-input-field' : ''}
      style={view === 'employee' ? { height: '90px' } : {}}
    >
      {view === 'question-bank' && (
        <div
          className="d-flex align-items-center justify-content-between"
          style={{ gap: ' 0.5em' }}
        >
          <div className="d-flex align-items-center">
            <SelectTemplateDropDown type="questionBank" />
          </div>
          <SearchField {...props} />
        </div>
      )}
      {view !== 'question-bank' && (
        <div>
          <SearchField {...props} />
        </div>
      )}
      {view === 'census' && (
        <div className="d-flex align-items-center">
          <UnlistedRoundBtn />
        </div>
      )}
      {(view?.includes('issues') ||
        view?.includes('feedback') ||
        view?.includes('stoplight') ||
        view === 'patient/rounds' ||
        view === 'employee' ||
        view === 'employee/rounds' ||
        view === 'users' ||
        view === 'patient/census' ||
        view === 'employee/frequency' ||
        (view?.includes('csc/list') && props.isShiftStatusDataAvailable)) && (
        <div className="w-100">
          <FilterBtn />
        </div>
      )}
      {(view?.includes('bssr/list') ||
        view?.includes('aidet/list') ||
        view?.includes('nlr/list') ||
        view?.includes('csc/list') ||
        view?.includes('cned/list') ||
        view === 'list/adhoc') && (
        <div className="d-flex align-items-center">
          <SearchEmployeesBtn />
        </div>
      )}

      {view === 'patient/rounds' && (
        <HintSection>
          <NeuTooltip
            tip="Rounds equals the number of completed patient rounds (able to
              round plus unable to round) in the selected unit(s) for the selected 
              date range. The units and date range selections can be changed using 
              the drop-down menu and orange filter button."
          >
            <HelpIcon className="ms-1" color="blue" fill={false} size="1x">
              help
            </HelpIcon>
            <span className="neu-text-blue-60 ms-1">What is this report?</span>
          </NeuTooltip>
        </HintSection>
      )}
      {view === 'patient/census' && (
        <HintSection>
          <NeuTooltip tip="Census shows patients admitted/registered in the selected unit(s) for the selected date range. The units and date range selections can be changed using the filter button.">
            <HelpIcon className="ms-1" color="blue" fill={false} size="1x">
              help
            </HelpIcon>
            <span className="neu-text-blue-60 ms-1">What is this report?</span>
          </NeuTooltip>
        </HintSection>
      )}
      {(view === 'employee' || view === 'employee/rounds') && (
        <HintSection className="ms-2">
          <NeuTooltip
            tip="The Rounds Report displays all 30-60-90 and Standard rounds
              completed during the selected date range. The date range
              and round type selection can be changed using the drop-down and
              orange filter."
          >
            <HelpIcon className="ms-1" color="blue" fill={false} size="1x">
              help
            </HelpIcon>
            <span className="neu-text-blue-60 ms-1">What is this report?</span>
          </NeuTooltip>
        </HintSection>
      )}
      {view === 'employee/frequency' && (
        <HintSection>
          <NeuTooltip
            tip="Frequency count is calculated by the number of completed
                employee rounds per employee in each department. The date range 
                selections can be changed using the drop-down menu."
          >
            <HelpIcon className="ms-1" color="blue" fill={false} size="1x">
              help
            </HelpIcon>
            <span className="neu-text-blue-60 ms-1">What is this report?</span>
          </NeuTooltip>
        </HintSection>
      )}

      {view?.includes('issues') && (
        <div className="d-flex">
          <AddBtn content="Add Issue" handleAddClick={handleAddClick} />
          <DownloadBtn
            fileNamePrefix={`${taskRoundingTypePrefix}${view.replace(
              '/',
              '-'
            )}-list`}
          />
        </div>
      )}
      {view?.includes('stoplight') && (
        <div className="d-flex">
          <AddBtn content="Add Stoplight" handleAddClick={handleAddClick} />
          <DownloadBtn
            fileNamePrefix={`${taskRoundingTypePrefix}${view.replace(
              '/',
              '-'
            )}-list`}
          />
        </div>
      )}
      {view === 'users' && (
        // align the button group to right
        <div className="d-flex">
          <ProvisioningBtn />
          <ExportCsvBtn
            handleExportCsv={props.handleExportCsv}
            fileNamePrefix={`${view.replace('/', '-')}-list`}
          />
        </div>
      )}
      {view === 'question-bank' && (
        // align the button group to right
        <div className="d-flex align-items-center">
          {isEnterpriseAdmin && (
            <AddBtn content="Add Question" handleAddClick={handleAddClick} />
          )}
          <ExportCsvBtn
            fileNamePrefix={view.replace('/', '-')}
            handleExportCsv={props.handleExportCsv}
          />
        </div>
      )}
      {view === 'discharges' && (
        <div className="d-flex align-items-center">
          <DischargeBtn />
        </div>
      )}
      {view?.includes('feedback') && (
        <div className="d-flex align-items-center">
          <AddBtn content="Add Feedback" handleAddClick={handleAddClick} />
          <DownloadBtn
            fileNamePrefix={`${taskRoundingTypePrefix}${view.replace(
              '/',
              '-'
            )}-list`}
            downloadStoplight={props.handleExportCsv}
          />
        </div>
      )}

      {/* {view === 'employee' && (
        <div className="d-flex align-items-center ms-1">
          <ExportCsvBtn
            handleExportCsv={props.handleExportCsv}
            fileNamePrefix={`${view.split('/')[0]}-rounds-report`}
          />
        </div>
      )} */}
      {(view === 'patient/rounds' ||
        view === 'patient/census' ||
        view === 'employee/rounds' ||
        view === 'employee/frequency') && (
        <DownloadExportCsvBtn
          handleExportCsv={props.handleExportCsv}
          handleClickDownloadPDF={props.handleClickDownloadPDF}
          fileNamePrefix={`${view.split('/')[0]}-${
            view === 'patient/census'
              ? 'census'
              : view === 'employee/frequency'
              ? 'frequency'
              : 'rounds'
          }-report`}
        />
      )}
    </SearchHeaderBar>
  );
};

export default SearchHeader;
